import { useState, useCallback } from "react";
import { useBreadcrumbs } from "@app/components/Breadcrumbs";
import {
  ActionButton,
  TextField,
  Text,
  Image,
  Skeleton,
  Card,
  CardPreview,
  Content,
} from "@react-spectrum/s2";
import { useUploadFile, usePresignURL, s3Key } from "@app/hooks/s3";
import { useCropImage } from "@app/hooks/photoshop";
import { DEFAULT_CROP_OBJECTS } from "@app/services/photoshop";

import {
  getImageBlobDimensions,
  convertImageBlobToType,
  cropImageBlobToBbox,
} from "@app/utils/imageBlob";
import { getImageBlob } from "@app/services/downloadFile";

import { ImageAsset } from "@app/components/ImageAsset";

import CropIcon from "@react-spectrum/s2/icons/Crop";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const TEST_FILE_PATH = "temp/image-crop/original";

const sizes = [
  { label: "Square", width: 512, height: 512 },
  { label: "Fullscreen", width: 1920, height: 1080 },
  { label: "Fb post", width: 1200, height: 630 },
  { label: "Ig post", width: 1080, height: 1350 },
  { label: "Reel", width: 1080, height: 1920 },
  { label: "Banner", width: 320, height: 1024 },
  { label: "Wide", width: 1080, height: 240 },
  { label: "Narrow", width: 240, height: 1080 },
];

export const CropImage = () => {
  useBreadcrumbs([{ label: "Admin", href: "/admin" }, { label: "Image Crop" }]);

  const [images, setImages] = useState([]);
  const [objects, setObjects] = useState(DEFAULT_CROP_OBJECTS);

  const presignURL = usePresignURL(TEST_FILE_PATH);
  const uploadFile = useUploadFile();

  const cropImage = useCropImage({
    onSuccess: async (data) => {
      const imageBlob = await getImageBlob({
        url: uploadFile.data?.presignURL ?? presignURL.data?.presignURL,
      });

      for (const bbox of data.crops[0].bboxes) {
        const croppedBlob = await cropImageBlobToBbox(imageBlob.blob, { bbox });
        setImages((prev) => [
          ...(prev || []),
          URL.createObjectURL(croppedBlob),
        ]);
      }
    },
  });

  const getImageDragOperation = useCallback(({ types }) => {
    if (Array.from(types)[0].startsWith("image/")) {
      return "copy";
    }

    return "cancel";
  }, []);

  const handleDrop = useCallback(
    async (items, name) => {
      const file =
        items[0] instanceof File ? items[0] : await items[0].getFile();

      if (file) {
        const { width, height } = await getImageBlobDimensions(file);
        const imgFile = await convertImageBlobToType(file, "jpeg");

        // setSize({ width: width / 2, height: height * 3 });

        uploadFile.mutate(
          {
            fileBlob: imgFile,
            filePath: TEST_FILE_PATH,
          },
          {
            onSuccess: (data, variables, onMutateResult, { client }) => {
              client.setQueryData(s3Key.presignURL(TEST_FILE_PATH), data);
            },
          },
        );
      }
    },
    [uploadFile.mutate],
  );

  return (
    <div
      className={style({
        display: "flex",
        flexDirection: "column",
        gap: 16,
        size: "full",
      })}
    >
      <div className={style({ font: "heading" })}>Crop image</div>

      <div className={style({ display: "flex", gap: 32 })}>
        <ImageAsset
          name={"test"}
          label={"Test"}
          contextualHelp={null}
          filePath={presignURL.data?.filePath}
          src={presignURL.data?.presignURL}
          isFilled={true}
          isLoading={uploadFile.isPending}
          onDrop={handleDrop}
          getImageDragOperation={getImageDragOperation}
        />

        <div>
          <TextField
            label="Crop focus"
            placeholder={DEFAULT_CROP_OBJECTS}
            description={`Accepts arbitrary natural-language labels (e.g. "person", "car.shoe", "red sports car")`}
            value={objects}
            onChange={setObjects}
          />
        </div>
      </div>

      <ActionButton
        isDisabled={!presignURL.data || cropImage.isPending}
        onPress={() =>
          cropImage.mutate({
            items: [[presignURL.data?.presignURL, sizes, objects.trim()]],
          })
        }
      >
        <CropIcon />
        <Text>Crop</Text>
      </ActionButton>

      <div
        className={style({
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 16,
          marginTop: 16,
          alignItems: "center",
        })}
      >
        <div>
          <Card variant="quiet" styles={style({ size: "full" })}>
            <CardPreview>
              <Image
                src={presignURL.data?.presignURL}
                alt={`original image`}
                styles={style({ aspectRatio: "square", objectFit: "contain" })}
              />
            </CardPreview>
            <Content>
              <Text slot="title">Original image</Text>
            </Content>
          </Card>
        </div>
        {sizes.map((size, idx) => (
          <Skeleton
            key={`${size.width}x${size.height}`}
            isLoading={cropImage.isPending}
          >
            <div>
              <Card variant="quiet" styles={style({ size: "full" })}>
                <CardPreview>
                  <Image
                    src={images[idx]}
                    alt={`cropped image ${size.width}x${size.height}`}
                    styles={style({
                      aspectRatio: "square",
                      objectFit: "contain",
                    })}
                    UNSAFE_style={
                      {
                        // aspectRatio: `${size.width} / ${size.height}`,
                      }
                    }
                  />
                </CardPreview>
                <Content>
                  <Text slot="title">{`${size.label} – ${size.width} x ${size.height}`}</Text>
                </Content>
              </Card>
            </div>
          </Skeleton>
        ))}
      </div>
    </div>
  );
};
